export type CookieConsent = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
  updatedAt: string;
};

export const COOKIE_CONSENT_STORAGE_KEY = "ctribe_cookie_consent";
export const COOKIE_CONSENT_EVENT = "ctribe-cookie-consent-changed";
export const COOKIE_SETTINGS_EVENT = "ctribe-cookie-settings-open";
// Shared with every *.ctribefestival.com site (www, verticals, CLabs) so one choice
// applies everywhere. Keep the name and format in sync with the marketing site (C-Tribe repo, src/lib/cookie-consent.ts).
export const COOKIE_CONSENT_COOKIE = "ctribe_consent";
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function sharedCookieAttributes(): string {
  const host = window.location?.hostname ?? "";
  const domain =
    host === "ctribefestival.com" || host.endsWith(".ctribefestival.com")
      ? "; domain=.ctribefestival.com"
      : "";
  return window.location?.protocol === "https:" ? `${domain}; Secure` : domain;
}

function readSharedCookie(): string | null {
  const match = document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${COOKIE_CONSENT_COOKIE}=`));
  return match ? decodeURIComponent(match.slice(COOKIE_CONSENT_COOKIE.length + 1)) : null;
}

export function buildConsent(
  overrides: Partial<Omit<CookieConsent, "essential" | "updatedAt">> = {}
): CookieConsent {
  return {
    essential: true,
    analytics: false,
    marketing: false,
    personalization: false,
    updatedAt: new Date().toISOString(),
    ...overrides,
  };
}

export function readCookieConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;

  try {
    const raw =
      readSharedCookie() ?? window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    return {
      essential: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      personalization: Boolean(parsed.personalization),
      updatedAt:
        typeof parsed.updatedAt === "string"
          ? parsed.updatedAt
          : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function writeCookieConsent(consent: CookieConsent): void {
  if (typeof window === "undefined") return;

  const value = JSON.stringify(consent);
  try {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, value);
  } catch {
    // Storage can be blocked; the shared cookie below still records the choice.
  }
  document.cookie = `${COOKIE_CONSENT_COOKIE}=${encodeURIComponent(value)}; path=/; max-age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${sharedCookieAttributes()}`;
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: consent }));
}

export function openCookieSettings(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
}
